const ROOT_SELECTOR = '[data-collection-filters]'
const RESULTS_SELECTOR = '[data-collection-results]'
const LINK_SELECTOR = '[data-filter-link]'

const SECTION_ATTRIBUTE = 'data-collection-section-id'
const TRANSITION_ATTRIBUTE = 'data-transitioning'

const FADE_MS = 200

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * @param {string} href
 * @param {string} sectionId
 * @returns {string}
 */
function buildRequestUrl(href, sectionId) {
  const url = new URL(href, window.location.origin)
  url.searchParams.set('section_id', sectionId)
  return url.toString()
}

/**
 * @param {string} requestUrl
 * @returns {Promise<string>}
 */
async function fetchSection(requestUrl) {
  const response = await fetch(requestUrl)
  if (!response.ok) {
    throw new Error(
      `filter request answered ${response.status}, expected 200 for ${requestUrl}`
    )
  }
  return response.text()
}

/**
 * @param {string} markup
 * @returns {string}
 */
function readResults(markup) {
  const parsed = new DOMParser().parseFromString(markup, 'text/html')
  const results = parsed.querySelector(RESULTS_SELECTOR)
  if (!results) {
    throw new Error(
      `filter response carries no ${RESULTS_SELECTOR}, expected the collection results wrapper`
    )
  }
  return results.innerHTML
}

/**
 * @returns {void}
 */
function refreshScrollables() {
  window.ScrollTrigger?.refresh?.()
  window.lenis?.resize?.()
}

/**
 * @param {HTMLElement} root
 * @param {string} href
 * @param {boolean} [push]
 * @returns {Promise<void>}
 */
async function swapResults(root, href, push = true) {
  const results = root.querySelector(RESULTS_SELECTOR)
  const sectionId = root.getAttribute(SECTION_ATTRIBUTE)
  if (!(results instanceof HTMLElement && sectionId)) return

  root.setAttribute(TRANSITION_ATTRIBUTE, 'out')

  try {
    const [markup] = await Promise.all([
      fetchSection(buildRequestUrl(href, sectionId)),
      wait(FADE_MS)
    ])
    results.innerHTML = readResults(markup)
    if (push) window.history.pushState({ href }, '', href)
  } catch {
    window.location.assign(href)
    return
  } finally {
    root.removeAttribute(TRANSITION_ATTRIBUTE)
  }

  refreshScrollables()
}

/**
 * @param {MouseEvent} event
 * @returns {void}
 */
function onDocumentClick(event) {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  if (!(event.target instanceof Element)) return

  const link = event.target.closest(LINK_SELECTOR)
  if (!(link instanceof HTMLAnchorElement)) return

  const root = link.closest(ROOT_SELECTOR)
  if (!(root instanceof HTMLElement)) return

  event.preventDefault()
  swapResults(root, link.href)
}

/**
 * @returns {void}
 */
function onPopState() {
  const root = document.querySelector(ROOT_SELECTOR)
  if (root instanceof HTMLElement) {
    swapResults(root, window.location.href, false)
  }
}

/**
 * @returns {void}
 */
function init() {
  document.addEventListener('click', onDocumentClick)
  window.addEventListener('popstate', onPopState)
}

init()

export { buildRequestUrl, readResults, swapResults }
